import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import bgImgDefault1 from "../../../assets/real_estate_template_bg_1.jpg";
import bgImgDefault2 from "../../../assets/real_estate_template_bg_2.jpg";
import bgImgDefault3 from "../../../assets/real_estate_template_bg_3.jpg";
import FormComponent from "./FormComponent";
import logo_placeholder from "../../../assets/logo_placeholder.png";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";

const Wrapper = styled(Box)(() => ({
  width: "100%",
  height: "100vh",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "center",
  "&:hover .react-multiple-carousel__arrow": {
    opacity: 1,
    visibility: "visible",
  },
}));

const BgLayer = styled(Box)(() => ({
  position: "absolute",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  zIndex: 1,
  pointerEvents: "none",
}));

const BgSlide = styled(Box)(() => ({
  width: "100%",
  height: "100vh",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
}));

const CarouselWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  inset: 0,
  zIndex: 3,

  ".react-multi-carousel-track": {
    height: "100%",
  },

  ".react-multi-carousel-item": {
    height: "100%",
  },

  ".react-multiple-carousel__arrow": {
    zIndex: 20,
    opacity: 0,
    visibility: "hidden",
    transition: "opacity 0.3s ease, visibility 0.3s ease",
    background: "rgba(0,0,0,0.5)",

    "&:hover": {
      background: "rgba(0,0,0,0.8)",
    },

    [theme.breakpoints.down("sm")]: {
      // Hide arrow wrappers in mobile
      ".custom-arrow-wrapper": {
        display: "none !important",
      },
    },

    //move arrow towards top
    [theme.breakpoints.down("md")]: {
      top: "24%",
      transform: "none !important", 
      opacity: "1 !important", 
      visibility: "visible !important",
      transform: "scale(0.5)",
    },
  },

  [theme.breakpoints.down("md")]: {
    ".custom-arrow-wrapper": {
      top: "24% !important",
      transform: "none !important",
      opacity: "1 !important",
      transform: "scale(0.5)",
    },
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 3,
  width: "85%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-evenly",
  [theme.breakpoints.down("sm")]: { width: "95%" },
}));

const Logo = styled("img")(({ theme }) => ({
  maxHeight: "48px",
  width: "auto",
  [[theme.breakpoints.between("xs", "md")]]: {
    maxWidth: "45%",
  },
  [[theme.breakpoints.between("md", "lg")]]: {
    maxWidth: "45%",
  },
  [theme.breakpoints.up("lg")]: {
    maxWidth: "40%",
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: "85%",
  margin: "auto",
  zIndex: 3,
  display: "flex",
  alignItems: "center",
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: { width: "95%" },
}));

const HeroText = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  color: "#fff",

  width: "100%",

  [theme.breakpoints.up("md")]: {
    width: "50%",
  },

  [theme.breakpoints.up("lg")]: {
    width: "40%",
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: '"Nunito Sans", "Open Sans", sans-serif',
  fontWeight: 800,
  lineHeight: 1.05,
  color: "#fff",
  textAlign: "left",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("sm")]: { fontSize: 32, letterSpacing: 1.15 },
  [theme.breakpoints.between("sm", "md")]: {
    fontSize: 48,
    letterSpacing: 1.15,
  },
  [theme.breakpoints.between("md", "lg")]: { fontSize: 64, letterSpacing: 1.5 },
  [theme.breakpoints.up("lg")]: { fontSize: 68, letterSpacing: 1.5 },
  [theme.breakpoints.up("xl")]: { fontSize: 76, letterSpacing: 1.5 },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Inter", "Open Sans", sans-serif',
  fontWeight: 300,
  color: "#fff",
  width: "90%",
  lineHeight: 1.4,
  textAlign: "left",
  [theme.breakpoints.down("sm")]: { fontSize: 14 },
  [theme.breakpoints.up("md")]: { fontSize: 18 },
}));

const FormWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "flex-start",
  [theme.breakpoints.down("md")]: {
    justifyContent: "center",
  },
}));

const MainSection = ({
  logoURL,
  contentState,
  formState,
  primaryColor,
  bgURLs = [],
}) => {
  const bgImages =
    bgURLs.length === 0
      ? [bgImgDefault1, bgImgDefault2, bgImgDefault3]
      : bgURLs; //default 3 images if no image was provided

  const responsive = {
    all: {
      breakpoint: { max: 4000, min: 0 },
      items: 1,
    },
  };

  return (
    <Wrapper>
      {/* Background Carousel */}
      <CarouselWrapper>
        <Carousel
          responsive={responsive}
          infinite
          autoPlay={bgImages.length > 1}
          autoPlaySpeed={5000}
          arrows={bgImages.length > 1}
          showDots={false}
          draggable={false}
          pauseOnHover={false}
          keyBoardControl
          transitionDuration={800}
          containerClass="carousel-container"
          itemClass="carousel-item"
          customLeftArrow={
            bgImages.length > 1 && (
              <Box
                className="custom-arrow-wrapper"
                sx={{
                  position: "absolute",
                  left: "2%",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 20,
                }}
              >
                <Box
                  component="button"
                  sx={{
                    backgroundColor: "#fff",
                    color: "#000",
                    opacity: 0,
                    borderRadius: "50%",
                    width: 48,
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    boxShadow: "0 3px 8px rgba(0,0,0,0.25)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                      transform: "scale(1.05)",
                      opacity: 1,
                    },
                  }}
                >
                  <ArrowBackIosNew />
                </Box>
              </Box>
            )
          }
          customRightArrow={
            bgImages.length > 1 && (
              <Box
                className="custom-arrow-wrapper"
                sx={{
                  position: "absolute",
                  right: "2%",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 20,
                }}
              >
                <Box
                  component="button"
                  sx={{
                    backgroundColor: "#fff",
                    color: "#000",
                    opacity: 0,
                    borderRadius: "50%",
                    width: 48,
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    boxShadow: "0 3px 8px rgba(0,0,0,0.25)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                      transform: "scale(1.05)",
                      opacity: 1,
                    },
                  }}
                >
                  <ArrowForwardIos />
                </Box>
              </Box>
            )
          }
        >
          {bgImages.map((img, idx) => (
            <BgSlide
              key={idx}
              sx={{
                backgroundImage: `url(${img || bgImgDefault1})`,
              }}
            />
          ))}
        </Carousel>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            zIndex: 2, // below arrows but above slides
            pointerEvents: "none",
          }}
        />
      </CarouselWrapper>

      <BgLayer />

      <LogoContainer>
        <Logo src={logoURL || logo_placeholder} alt="Logo" />
      </LogoContainer>

      <ContentWrapper>
        <HeroText>
          <Title>{contentState?.title}</Title>
          <SubTitle>{contentState?.sub_title}</SubTitle>
        </HeroText>

        <FormWrapper>
          <Box sx={{ width: "100%" }}>
            <FormComponent
              formState={formState}
              contentState={contentState}
              primaryColor={primaryColor}
            />
          </Box>
        </FormWrapper>
      </ContentWrapper>
    </Wrapper>
  );
};

export default MainSection;
